import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import { useTranslation } from '../hooks/useTranslation';

const CompanyBottomNavigation = () => {
    const location = useLocation();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const isEmployeeRole = (user?.user_company_role || user?.company_role || user?.roleInCompany) === 'employee';

    const navItems = [
        {
            path: '/company/vacancies',
            label: t('company.navigation.vacancies'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
            ),
            activeIcon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
            )
        },
        {
            path: '/company/subscriptions',
            label: t('company.navigation.subscriptions'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="14" rx="2" ry="2" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                    <path d="M7 15h.01M11 15h.01M15 15h.01" />
                </svg>
            ),
            activeIcon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="14" rx="2" ry="2" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                    <path d="M7 15h.01M11 15h.01M15 15h.01" />
                </svg>
            )
        },
        {
            path: '/company/vacancies/create',
            label: t('company.navigation.addVacancy'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
            ),
            activeIcon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
            )
        },
        {
            path: '/company/plans',
            label: t('company.navigation.plans'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 22h20L12 2z" />
                </svg>
            ),
            activeIcon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 22h20L12 2z" />
                </svg>
            )
        },
        {
            path: '/company/profile',
            label: t('company.navigation.profile'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                </svg>
            ),
            activeIcon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                </svg>
            )
        }
    ];

    const filteredNavItems = isEmployeeRole
        ? navItems.filter(item => !['/company/vacancies/create', '/company/plans', '/company/subscriptions'].includes(item.path))
        : navItems;

    const isActive = (path) => {
        if (path === '/company/vacancies') {
            return location.pathname === '/company/vacancies' ||
                   location.pathname.startsWith('/company/vacancies/') &&
                   !location.pathname.includes('/create') &&
                   !location.pathname.includes('/applications');
        }
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    return (
        <nav className="bottom-navigation" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            <div className="bottom-nav-container">
                {filteredNavItems.map((item) => {
                    const active = isActive(item.path);
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`bottom-nav-item ${active ? 'active' : ''}`}
                        >
                            <div className="nav-icon-wrapper">
                                {active ? item.activeIcon : item.icon}
                            </div>
                            <span className="nav-label">{item.label}</span>
                            {active && <div className="nav-indicator" />}
                        </Link>
                    );
                })}
            </div>
        </nav>
    );
};

export default CompanyBottomNavigation;

